import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Course } from '../types/course';
import { fetchSuggestedCourses } from '../services/suggestedCoursesService';
import HomeHero from '../components/home/HomeHero';
import HomeWelcomeBox from '../components/home/HomeWelcomeBox';
import WhyChooseBanner from '../components/home/WhyChooseBanner';
import HomeCtaBanner from '../components/home/HomeCtaBanner';
import HomeFaq from '../components/home/HomeFaq';
import SuggestedCoursesSection from '../components/home/SuggestedCoursesSection';
import StudySupportBanner from '../components/home/StudySupportBanner';
import GradeCards from '../components/common/GradeCards';
import HomeSectionFrame from '../components/home/HomeSectionFrame';
import WhatsAppButton from '../components/common/WhatsAppButton';
import HomePopup from './homepop-up';

const Home: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [recommendedCourses, setRecommendedCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  useEffect(() => {
    setMounted(true);
    setShowPopup(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSuggested = async () => {
      try {
        setLoadingCourses(true);
        const courses = await fetchSuggestedCourses(1, 8);
        if (!cancelled) setRecommendedCourses(courses);
      } catch (error) {
        console.error('Error fetching suggested courses:', error);
        if (!cancelled) setRecommendedCourses([]);
      } finally {
        if (!cancelled) setLoadingCourses(false);
      }
    };

    loadSuggested();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="min-h-screen overflow-x-hidden bg-[linear-gradient(180deg,#EEF1F8_0%,#F4F6FC_45%,#FFFFFF_100%)]"
      dir="rtl"
    >
      <HomePopup isOpen={showPopup} onClose={() => setShowPopup(false)} />
      <HomeHero />

      <div className="relative z-10 -mt-4 space-y-1 sm:-mt-6">
        <HomeWelcomeBox />

        <SuggestedCoursesSection courses={recommendedCourses} loading={loadingCourses} />

        <WhyChooseBanner />

        <HomeSectionFrame id="grades-section" aria-labelledby="grades-heading" tone="paper">
          <div className="mb-8 max-w-3xl md:mb-10">
            <div className="mb-3 flex items-center gap-2.5">
              <span className="inline-block h-5 w-1 shrink-0 rounded-full bg-brand-500" aria-hidden />
              <p className="text-sm font-semibold text-brand-700">الصفوف الدراسية</p>
            </div>
            <h2
              id="grades-heading"
              className="text-[clamp(1.6rem,1.5vw+1.1rem,2.35rem)] font-extrabold text-slate-900"
            >
              اختار صفّك وابدأ
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base sm:leading-7">
              كورسات وحصص مرتّبة حسب الصف — اختار مستواك وادخل على المحتوى المناسب ليك.
            </p>
          </div>
          <GradeCards />
        </HomeSectionFrame>

        <StudySupportBanner />

        <HomeFaq />

        <HomeCtaBanner />
      </div>

      <div className="fixed bottom-6 left-6 z-40">
        <WhatsAppButton
          message="مرحباً، أريد الاستفسار عن المنصة"
          showText={false}
          size="lg"
          className="!rounded-full !p-4 shadow-2xl"
        />
      </div>

      {mounted && !isAuthenticated && (
        <button
          type="button"
          onClick={() => navigate('/register')}
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-brand-600 px-5 py-3 text-sm font-bold text-white shadow-2xl transition hover:scale-105 hover:bg-brand-500 sm:hidden"
        >
          سجّل الآن
        </button>
      )}
    </div>
  );
};

export default React.memo(Home);
